import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import ClockCheckIcon from '../../../../assets/icons/clock-check-icon.svg';
import ClockIcon from '../../../../assets/icons/clock-icon.svg';
import GreenClock from '../../../../assets/icons/green-clock.svg';
import InfoIcon from '../../../../assets/icons/info-circle-icon.svg';
import SeatIcon from '../../../../assets/icons/seat-icon.svg';
import YellowClock from '../../../../assets/icons/yellow-clock.svg';
import { CustomButton } from '../../../../components/Button';
import { CustomChip } from '../../../../components/Chip';
import { LANG_TEXT } from '../../../../constants/language';
import { convertText, formatFirebaseTime } from '../../../../utils/common';
import { formatSeatInfo } from '../../../../utils/multiOrder.utils';
import { STATUS_COLOR_MAP } from '../../data';
import { Order, OrderItemStatus, Restaurant } from '../../order.types';

interface OrderItemsProps {
  item: Order;
  disabled?: boolean;
  onClickCTA?: (item: Order) => void;
  onShowInfo: (item: Order) => void;
  onDamage: (item: Order, restaurantId?: string) => void;
  restaurantList: Restaurant[];
  seatDataMap?: Map<string, any>;
}

const OrderItems: React.FC<OrderItemsProps> = ({ item, onClickCTA, onShowInfo, disabled, onDamage, restaurantList, seatDataMap }) => {
  const { t } = useTranslation();

  const information = useMemo(() => {
    if (!item.order_table_number || !seatDataMap) return null;
    return seatDataMap.get(item.order_table_number) || null;
  }, [item.order_table_number, seatDataMap]);

  const renderButton = useMemo(() => {
    if (!item?.rider_id)
      return (
        <CustomButton
          variant="contained"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClickCTA && onClickCTA(item);
          }}
        >
          {t(LANG_TEXT.PICK_UP_ORDER)}
        </CustomButton>
      );
    switch (item.order_status) {
      case OrderItemStatus.PREPARING:
      case OrderItemStatus.DAMAGED_PREPARING: {
        return (
          <CustomButton variant="outlined" disabled startIcon={<img src={ClockIcon} width={17} height={17} alt="user" />}>
            {t(LANG_TEXT.PREPARING)}
          </CustomButton>
        );
      }
      case OrderItemStatus.DELIVERING:
      case OrderItemStatus.DAMAGED_DELIVERING: {
        return (
          <CustomButton variant="outlined" disabled startIcon={<img src={ClockIcon} width={17} height={17} alt="user" />}>
            {t(LANG_TEXT.DELIVERING)}
          </CustomButton>
        );
      }
      case OrderItemStatus.READY_FOR_PICK_UP:
      case OrderItemStatus.DAMAGED_READY_FOR_PICK_UP: {
        return (
          <CustomButton
            variant="contained"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onClickCTA && onClickCTA(item);
            }}
          >
            {t(LANG_TEXT.PICKED_UP_FOOD)}
          </CustomButton>
        );
      }

      default: {
        return null;
      }
    }
  }, [disabled, item, onClickCTA, t]);

  const status = useMemo(() => {
    const statusMap = {
      [OrderItemStatus.PREPARING]: t(LANG_TEXT.PREPARING),
      [OrderItemStatus.DAMAGED_PREPARING]: t(LANG_TEXT.PREPARING),
      [OrderItemStatus.READY_FOR_PICK_UP]: t(LANG_TEXT.READY_FOR_PICK_UP),
      [OrderItemStatus.DAMAGED_READY_FOR_PICK_UP]: t(LANG_TEXT.READY_FOR_PICK_UP),
      [OrderItemStatus.DELIVERING]: t(LANG_TEXT.DELIVERING),
      [OrderItemStatus.DAMAGED_DELIVERING]: t(LANG_TEXT.DELIVERING),
      [OrderItemStatus.DELIVERED]: t(LANG_TEXT.DELIVERED),
    };
    return convertText(item?.rider_id ? statusMap[item.order_status] : t(LANG_TEXT.NEW));
  }, [item, t]);

  const restaurant = useMemo(() => {
    const restaurantId = item?.multi_orders?.[0].restaurant_id;
    const restaurant = restaurantList?.find((r) => r.restaurant_id === restaurantId);

    return item.type === 'promotion' ? item?.restaurant_promotion : restaurant || item?.restaurant;
  }, [item, restaurantList]);

  const foodsList = useMemo(() => {
    const restaurantFoods = item.foods || [];
    const promotionFoods = item.promotion_foods || [];
    const allFoods = [...restaurantFoods, ...promotionFoods];

    return allFoods.filter((food) => {
      const quantity = food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity;
      return quantity > 0;
    });
  }, [item.foods, item.promotion_foods]);

  const deliveryRequestValue = item.delivery_request || 'WHEN_ITS_READY';
  const isWhenItsReady = deliveryRequestValue === 'WHEN_ITS_READY';
  const deliveryClockIcon = isWhenItsReady ? GreenClock : YellowClock;
  const deliveryTextColor = isWhenItsReady ? 'var(--Success-600, #3D9F4A)' : 'var(--Warning-600, #FDB022)';
  const deliveryRequestDisplay = isWhenItsReady ? t(deliveryRequestValue) : deliveryRequestValue;
  const preOrderDeliveryDisplay =
    isWhenItsReady || !item.requested_delivery_time
      ? t(deliveryRequestValue)
      : deliveryRequestValue || dayjs(item.requested_delivery_time.toDate()).format('HH:mm');

  return (
    <Box
      sx={{ opacity: disabled ? 0.4 : 1, backgroundColor: 'white', borderRadius: '8px' }}
      padding="16px"
      borderBottom="1px solid #EAECF0"
      role="button"
      onClick={() => {
        if (!disabled) onShowInfo(item);
      }}
    >
      <Box display="flex" flexDirection="column" gap="24px">
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <CustomChip size="md" bgcolor="#F2F4F7" color="#344054" height="max-content">
              {item.id} {item.has_damaged_order ? ` (${t(LANG_TEXT.DAMAGED)})` : ''}
            </CustomChip>
            <Box display="flex" flexDirection="row" gap="8px">
              {(item.order_status === OrderItemStatus.DELIVERING || item.order_status === OrderItemStatus.DAMAGED_DELIVERING) && (
                <CustomButton
                  variant="contained"
                  padding="0 6px"
                  sx={{ fontSize: 14, height: 32 }}
                  onClick={async (e) => {
                    onDamage(item);
                    e.stopPropagation();
                  }}
                >
                  {t(LANG_TEXT.DAMAGED_FOOD)}
                </CustomButton>
              )}
              <CustomChip
                size="md"
                bgcolor={STATUS_COLOR_MAP[item?.rider_id ? item.order_status : OrderItemStatus.PREPARING].bgColor}
                color={STATUS_COLOR_MAP[item?.rider_id ? item.order_status : OrderItemStatus.PREPARING].color}
                textTransform="capitalize"
                height="max-content"
              >
                {status}
              </CustomChip>
            </Box>
          </Box>

          <Box
            display="inline-flex"
            justifyContent="center"
            alignItems="center"
            gap="8px"
            bgcolor="#F2F4F7"
            color="#344054"
            borderRadius="999px"
            padding="2px 4px"
            width="fit-content"
            mx="auto"
          >
            {!item.is_pre_order && (
              <Box display="flex" alignItems="center" gap="4px">
                <img src={deliveryClockIcon} height={16} width={16} alt="DeliveryClock" />
                <Typography fontSize={12} lineHeight={'20px'} color={deliveryTextColor}>
                  {deliveryRequestDisplay}
                </Typography>
              </Box>
            )}
            {item.is_pre_order && item.requested_delivery_time && (
              <Box display="flex" alignItems="center" gap="4px">
                <img src={deliveryClockIcon} height={16} width={16} alt="DeliveryClock" />
                <Typography fontSize={12} lineHeight={'20px'} color={deliveryTextColor}>
                  {preOrderDeliveryDisplay}
                </Typography>
              </Box>
            )}
          </Box>

          <Box display="flex" flexDirection="column" gap="4px">
            <Typography color="#475467" lineHeight="28px" fontWeight={600} fontSize="18px">
              {restaurant?.name || '-'}
            </Typography>
            {foodsList.map((food, foodIndex) => {
              const quantity = food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity;
              return (
                <Typography key={foodIndex} color="#475467" lineHeight="18px" fontWeight={400} fontSize="14px">
                  {food.item_name}: {quantity} {t(LANG_TEXT.ORDER_ITEMS)}
                </Typography>
              );
            })}
          </Box>

          <Box display="flex" alignItems="center" justifyContent="start">
            <Box display="flex" gap="8px" alignItems="center">
              <img src={SeatIcon} width={24} height={24} alt="user" />
              <Typography color="#475467" lineHeight="24px" fontWeight={500} fontSize="16px">
                {information ? formatSeatInfo(information) : '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
        {renderButton}
      </Box>
      {disabled && (
        <Box display="flex" gap="8px" marginTop="8px">
          <img src={InfoIcon} width={14} height={14} alt="user" />
          <Typography color="#667085" lineHeight="18px" fontWeight={400} fontSize="12px">
            {t(LANG_TEXT.ORDER_DISABLE_TOOLTIP)}
          </Typography>
        </Box>
      )}
      {item.order_status === OrderItemStatus.DELIVERED && (
        <Box display="flex" gap="8px" marginTop="16px">
          <img src={ClockCheckIcon} width={17} height={17} alt="user" />
          <Typography color="#98A2B3" lineHeight="20px" fontWeight={400} fontSize="14px">
            {t(LANG_TEXT.ORDER_DELIVERED_AT, {
              date: formatFirebaseTime(item?.order_delivered_time?.seconds),
              interpolation: { escapeValue: false },
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OrderItems;
